import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function KanbanBoard({ columns, items, groupKey, onMove, renderCard, colorFor }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const boardRef = useRef(null);

  const grouped = {};
  columns.forEach((c) => (grouped[c] = []));
  items.forEach((it) => {
    const k = it[groupKey];
    if (grouped[k]) grouped[k].push(it);
  });

  const updateEdges = useCallback(() => {
    const el = boardRef.current;
    if (!el) return;
    setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, []);

  useEffect(() => {
    updateEdges();
    const ro = new ResizeObserver(updateEdges);
    if (boardRef.current) ro.observe(boardRef.current);
    return () => ro.disconnect();
  }, [updateEdges, columns.length, items.length]);

  const scrollBy = (dir) => boardRef.current?.scrollBy({ left: dir * 304, behavior: "smooth" });

  // Auto-scroll the board when dragging near its left/right edge.
  const edgeScroll = (e) => {
    if (!dragId) return;
    const el = e.currentTarget, r = el.getBoundingClientRect();
    if (e.clientX > r.right - 80) el.scrollLeft += 24;
    else if (e.clientX < r.left + 80) el.scrollLeft -= 24;
  };

  const arrow = "h-8 w-8 rounded-md border border-border bg-card flex items-center justify-center text-muted-foreground transition-colors hover:text-primary hover:border-primary/40 disabled:opacity-30 disabled:pointer-events-none";

  return (
    <div>
      <div className={`flex items-center justify-end gap-2 mb-2 ${edges.left || edges.right ? "" : "invisible"}`} data-testid="kanban-scroll-controls">
        <button type="button" className={arrow} onClick={() => scrollBy(-1)} disabled={!edges.left}
          aria-label="Scroll left" data-testid="kanban-scroll-left"><ChevronLeft className="h-4 w-4" /></button>
        <button type="button" className={arrow} onClick={() => scrollBy(1)} disabled={!edges.right}
          aria-label="Scroll right" data-testid="kanban-scroll-right"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div ref={boardRef} onScroll={updateEdges} onDragOver={edgeScroll}
        className="flex gap-4 overflow-x-auto no-scrollbar pb-3" data-testid="kanban-board">
        {columns.map((col) => (
          <div
            key={col}
            onDragOver={(e) => { e.preventDefault(); setOverCol(col); }}
            onDragLeave={() => setOverCol((c) => (c === col ? null : c))}
            onDrop={() => { if (dragId) onMove(dragId, col); setDragId(null); setOverCol(null); }}
            className={`flex-1 min-w-[240px] max-w-[288px] shrink-0 rounded-md border bg-muted/30 flex flex-col transition-colors ${overCol === col ? "border-primary bg-primary/5" : "border-border"}`}
            data-testid={`kanban-col-${col.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="px-3 py-2.5 border-b border-border flex items-center justify-between sticky top-0">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: colorFor?.(col) || "hsl(var(--primary))" }} />
                <span className="text-xs font-semibold uppercase tracking-wide">{col}</span>
              </div>
              <span className="text-xs text-muted-foreground bg-background rounded-full px-1.5">{grouped[col].length}</span>
            </div>
            <div className="p-2 space-y-2 flex-1 min-h-[120px] overflow-y-auto slim-scroll no-scrollbar max-h-[calc(100vh-300px)]">
              {grouped[col].map((it) => (
                <div
                  key={it.id}
                  draggable
                  onDragStart={() => setDragId(it.id)}
                  onDragEnd={() => setDragId(null)}
                  data-testid={`kanban-card-${it.id}`}
                  className={`rounded-md border border-border bg-card p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${dragId === it.id ? "opacity-50" : ""}`}
                >
                  {renderCard(it)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
